/**
 * Device rows the daily-loop screens read (04 §3.3), as plain types over `SyncRow`. Field names are
 * keepgen DTOs of `@yk/sync-store` (`EntryRow`, `ObservationRow`, `TimerRow`, from
 * `schema/sync/dto.json`); this module is the one place that maps them for the screens.
 */
import type { EntryRow as EntryDto, ObservationRow as ObservationDto, SyncRow, TimerRow as TimerDto } from '@yk/sync-store';
import type { DataClass } from '@yk/core-wasm';

/** `MetricValue` (03 §2.4): one member is set according to `kind`. */
export interface MetricValue {
  kind: 'NUMBER' | 'TEXT' | 'CHOICE' | 'FLAG' | 'DURATION';
  number?: number | null;
  text?: string | null;
  choice?: string | null;
  flag?: boolean | null;
  durationSeconds?: number | null;
  unit?: string | null;
}

export interface ObservationSummary {
  id?: string;
  childId: string;
  metric: string;
  value: MetricValue;
}

export interface EntryRow {
  id: string;
  childIds: string[];
  /** `occurred.start` (UTC ISO); the local index (04 §3.3). */
  effectiveAt: string;
  endAt: string | null;
  authorId: string | null;
  observations: ObservationSummary[];
  note: string | null;
  source: string;
  status: 'RECORDED' | 'DELETED';
  /** "Not sent yet" (04 §3.4): cmids of outbox commands projected onto the row. */
  pending: boolean;
  cls: DataClass | string;
}

export interface ObservationRow {
  id: string;
  entryId: string;
  childId: string;
  metricCode: string;
  effectiveAt: string;
  value: MetricValue;
}

export type TimerState = 'RUNNING' | 'PAUSED' | 'STOPPED' | 'CANCELLED';

export interface TimerRow {
  id: string;
  childIds: string[];
  metric: string;
  startedAt: string;
  pausedAt: string | null;
  accumulatedSeconds: number;
  state: TimerState;
  startedById: string | null;
  endedAt: string | null;
  pending: boolean;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

/** A pulled row read through its DTO (fields may be absent on optimistic, not-yet-pulled rows). */
type Dto<T> = SyncRow & Partial<Omit<T, 'id' | 'v' | 'updated_seq'>>;

export function toEntry(row: SyncRow): EntryRow {
  const r = row as Dto<EntryDto>;
  const obs = Array.isArray(r['observations']) ? (r['observations'] as ObservationSummary[]) : [];
  return {
    id: r.id,
    childIds: strs(r['childIds']).length ? strs(r['childIds']) : r._childIds,
    effectiveAt: str(r['effectiveAt']) ?? '',
    endAt: str(r['endAt']),
    authorId: str(r['authorId']),
    observations: obs,
    note: str(r['note']),
    source: str(r['source']) ?? 'MANUAL',
    status: r['status'] === 'DELETED' ? 'DELETED' : 'RECORDED',
    pending: (r._pending?.length ?? 0) > 0,
    cls: r._class,
  };
}

export function toObservation(row: SyncRow): ObservationRow {
  const r = row as Dto<ObservationDto>;
  return {
    id: r.id,
    entryId: str(r['entryId']) ?? '',
    childId: str(r['childId']) ?? r._childIds[0] ?? '',
    metricCode: str(r['metricCode']) ?? '',
    effectiveAt: str(r['effectiveAt']) ?? '',
    value: (r['value'] as MetricValue | undefined) ?? { kind: 'FLAG', flag: true },
  };
}

export function toTimer(row: SyncRow): TimerRow {
  const r = row as Dto<TimerDto>;
  const state = String(r['state'] ?? 'RUNNING').toUpperCase();
  return {
    id: r.id,
    childIds: strs(r['childIds']).length ? strs(r['childIds']) : r._childIds,
    metric: str(r['metric']) ?? '',
    startedAt: str(r['startedAt']) ?? '',
    pausedAt: str(r['pausedAt']),
    accumulatedSeconds: typeof r['accumulatedSeconds'] === 'number' ? r['accumulatedSeconds'] : 0,
    state: (['RUNNING', 'PAUSED', 'STOPPED', 'CANCELLED'] as const).find((s) => s === state) ?? 'RUNNING',
    startedById: str(r['startedById']),
    endedAt: str(r['endedAt']),
    pending: (r._pending?.length ?? 0) > 0,
  };
}

/** Elapsed seconds from `startedAt` and the server-corrected clock (04 §3.11: never by counting ticks). */
export function timerElapsed(t: Pick<TimerRow, 'startedAt' | 'pausedAt' | 'accumulatedSeconds' | 'state'>, serverNow: number): number {
  const start = Date.parse(t.startedAt);
  if (!Number.isFinite(start)) return 0;
  if (t.state === 'PAUSED' && t.pausedAt) return t.accumulatedSeconds;
  return Math.max(0, t.accumulatedSeconds + Math.floor((serverNow - start) / 1000));
}
